import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import ButtonWithCloseIcon from '@components/ButtonWithCloseIcon'
import { Row, Col, Container, Icon } from '@mch-group/uikit-components'

import classNames from 'classnames'
import Locations from './LocationsFilter'
import AccessFilter from './AccessFilter'
import HosttypeFilter from './HosttypeFilter'
import DayFilter from './DayFilter'
import TypeFilter from './TypeFilter'
import EventTypeFilter from './EventTypeFilter'
import SortBy from './SortBy'
import { useModuleContext } from '../../contexts/EventsContext'
import usePush from '../../hooks/usePush'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'
import DayCalendarStyles from '../../styles/DayCalendarStyles'

/**
 * This component shows the list of Events filters.
 * Filters in mobile have a toggle, this is, implemented here.
 * Each filter can be hidden in CMS, implemented here.
 */
const Filters = () => {
  //@ts-ignore
  const [{ labels, filters, paginationInfo, CMSProps }] = useModuleContext()
  const { deleteAllFilters, activeFilters } = usePush()
  const [isMobileFiltersVisible, setIsMobileFiltersVisible] = useState(false)
  const toggleMobileFilters = useCallback(() => {
    setIsMobileFiltersVisible((visible) => !visible)
  }, [])
  const [scrolled, setScrolled] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const interval = setInterval(() => {
      if (ref.current && window?.location.href.includes('?')) {
        //@ts-ignore
        ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
        setScrolled(true)
      }
    }, 250)

    if (scrolled) {
      clearInterval(interval)
    }

    return () => clearInterval(interval)
  }, [scrolled])

  if (!filters || !labels) return null
  return (
    <form className='form'>
      {/* Some JSX to show/hide filters mobile (irrelevant in desktop) */}
      <FiltersToggleMobileStyle
        role='button'
        tabIndex={0}
        onClick={toggleMobileFilters}
        onKeyUp={toggleMobileFilters}
        className={classNames('mobile only tablet only', {
          'toggle--on': isMobileFiltersVisible
        })}
      >
        <span className='fw-bold'>
          <label htmlFor='filters'>{labels.Events.mobileFiltersToggleText || 'Filters'}</label>
          <Icon name='chevron-down' />
        </span>
      </FiltersToggleMobileStyle>
      <FiltersWrap
        className={classNames({ 'filters--off': !isMobileFiltersVisible }, { 'filters--on': isMobileFiltersVisible })}
      >
        <RowResetFiltersDesktopStyle className='mobile hidden tablet hidden'>
          <Col>
            {Object.keys(activeFilters()).length > 0 && (
              <ButtonWithCloseIcon onClick={deleteAllFilters} label={labels.Events.resetAllFilters} />
            )}
          </Col>
        </RowResetFiltersDesktopStyle>
        {CMSProps.aevs_grp1_location_filter_visibility !== 'true' && (
          <Locations
            //@ts-ignore
            locationsData={
              filters.reduce((sum, current) => ({ ...sum, [current.entityType]: current.options }), {}).LOCATION
            }
          />
        )}
        {/* Filters */}
        {/* This ugly lines will avoid rendering an empty Filters row, in case all are hidden */}
        {CMSProps.aevs_grp1_type_filter_visibility === 'true' &&
          CMSProps.aevs_grp1_day_filter_visibility === 'true' &&
          CMSProps.aevs_grp1_category_filter_visibility === 'true' &&
          CMSProps.aevs_grp1_host_type_filter_visibility === 'true' &&
          CMSProps.aevs_grp1_host_type_filter_visibility === 'true' &&
          CMSProps.aevs_grp1_access_filter_visibility === 'true' ? null : (
            <>
              <div className='pt-8'>
                <Container className='all-filters'>
                  <Row className='d-flex justify-content-between mt-0'>
                    {/* Type Filter */}
                    {CMSProps.aevs_grp1_type_filter_visibility !== 'true' && (
                    <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2 pe-xxl-0'>
                      <span>
                        <label htmlFor='types' className='fw-bold'>{labels.Events.browseFilterType}</label>
                        <TypeFilter />
                      </span>
                    </Col>
                  )}

                    {/* Day Filter */}
                    {CMSProps.aevs_grp1_day_filter_visibility !== 'true' && (
                    <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2  p-xxl-0'>
                      <DayFilter />
                    </Col>
                  )}

                    {/* Category Filter / Event Type */}
                    {CMSProps.aevs_grp1_category_filter_visibility !== 'true' && (
                    <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2  p-xxl-0 pt-8 pt-md-0'>
                      <span>
                        <label htmlFor='eventTypes' className='fw-bold'>{labels.Events.browseFilterCategory}</label>
                        <EventTypeFilter />
                      </span>
                    </Col>
                  )}

                    {/* Host Type Filter */}
                    {CMSProps.aevs_grp1_host_type_filter_visibility !== 'true' && (
                    <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2 d-none d-lg-block p-xxl-0'>
                      <span>
                        <label htmlFor='hostType' className='fw-bold'>{labels.Events.browseFilterHostType}</label>
                        <HosttypeFilter />
                      </span>
                    </Col>
                  )}

                    {/* Access Type Filter */}
                    {CMSProps.aevs_grp1_access_filter_visibility !== 'true' && (
                    <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2 d-none d-xxl-block p-xxl-0'>
                      <span>
                        <label htmlFor='access' className='fw-bold'>{labels.Events.browseFilterAccessType}</label>
                        <AccessFilter />
                      </span>
                    </Col>
                  )}
                  </Row>
                </Container>
              </div>
            </>
        )}
        {/* End of Filters */}
        {/* Sort By */}
        <Container>
          <Row className='sortby-row mt-4 mb-md-10'>
            {CMSProps.aevs_grp1_host_type_filter_visibility !== 'true' && (
            <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2  d-lg-none'>
              <span>
                <label htmlFor='hostType' className='fw-bold'>{labels.Events.browseFilterHostType}</label>
                <HosttypeFilter />
              </span>
            </Col>
                  )}
            {CMSProps.aevs_grp1_access_filter_visibility !== 'true' && (
              <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2 d-xxl-none'>
                <span>
                  <label htmlFor='access' className='fw-bold'>{labels.Events.browseFilterAccessType}</label>
                  <AccessFilter />
                </span>
              </Col>
            )}
            <Col className='col-sm-12 col-md-4 col-lg-3 col-xxl-2 pe-xxl-0'>
              <Wrapper>
                <label htmlFor='orderBy' className='fw-bold'>{labels.Events.browseFilterSortBy}</label>
                <SortBy />
              </Wrapper>
            </Col>
            {/* The counter for Only for Desktop. Design doesn't allow Responsive, so has to be Adaptative */}
            <Col
              textAlign='right'
              className='mobile hidden tablet hidden d-flex align-items-center justify-content-end'
            >
              <span>
                <div>&nbsp;</div>
                <div>
                  {paginationInfo.total} {labels.Events.browseFilterEventsCounter}
                </div>
              </span>
            </Col>
          </Row>
        </Container>
      </FiltersWrap>
      {/* The Reset filters, only for mobile. Again, design doesn't allow responsive */}
      <RowResetFiltersMobileStyle className='mobile only tablet only'>
        <Col>
          {Object.keys(activeFilters()).length > 0 && (
            <ButtonWithCloseIcon onClick={deleteAllFilters} label={labels.Events.resetAllFilters} />
          )}
        </Col>
      </RowResetFiltersMobileStyle>
      {/* The counter for mobile */}
      <span className='mobile only tablet only mb-4'>
        <div>&nbsp;</div>
        <div>
          {paginationInfo.total} {labels.Events.browseFilterEventsCounter}
        </div>
      </span>
      <div ref={ref}>&nbsp;</div>
      <DayCalendarStyles />
    </form>
  )
}

/** Some design adjustments */
const RowResetFiltersDesktopStyle = styled.div<any>`
  text-align: right;
  height: 0;
  position: relative;
  top: -60px;
`

const RowResetFiltersMobileStyle = styled(Row)`
  margin-left: -4px;
`

/* Only affects mobile */
const FiltersToggleMobileStyle = styled.div<any>`
  @media (max-width: 991px) {
    position: relative;
    top: 24px;
  }

  > .field {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > label {
      cursor: pointer;
    }
  }

  border-bottom: 1px solid ${tokens.color.light.base.neutrals['300'].value} !important;
  padding-bottom: 14px;
  margin-bottom: 40px;
  cursor: pointer;

  &.toggle--on {
    svg {
      transform: rotate(180deg);
    }
  }
`

const Wrapper = styled.span<any>`
  .button-delete {
    display: none;
  }
`

/** The List of Filters itself */
const FiltersWrap = styled.div<any>`
  &.filters {
    &--off {
      @media (max-width: 992px) {
        display: none;
      }
    }
  }

  .ui.five.all-filters {
    @media (max-width: 991px) {
      margin-bottom: -48px;
    }

    margin-bottom: 30px;

    > .row {
      > .column {
        @media (max-width: 991px) {
          margin-bottom: 24px;
        }
      }
    }
  }

  .ui.five > .row.sortby-row {
    justify-content: space-between;
    margin-bottom: 16px;
  }

  @media (max-width: 991px) {
    .ui.five > .row > .column {
      width: 100%;
    }
  }
`

export default Filters
